import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef, type ReactNode } from "react";

import { cn } from "@/shared/lib/cn";

gsap.registerPlugin(useGSAP);

export interface Merry3DCardProps {
  children: ReactNode;
  className?: string;
  interactionTarget?: HTMLElement | null;
  isActive?: boolean;
  maxRotation?: number;
  perspective?: number;
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function supportsFinePointer() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/**
 * 通用 3D 卡片容器。业务数据由调用方持有，组件仅管理指针透视与回弹。
 */
export function Merry3DCard({
  children,
  className,
  interactionTarget = null,
  isActive = true,
  maxRotation = 8,
  perspective = 1_000,
}: Merry3DCardProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const reducedMotion = prefersReducedMotion();

  useGSAP(
    () => {
      const body = bodyRef.current;
      if (!body) return;

      gsap.killTweensOf(body);
      gsap.set(body, {
        force3D: true,
        rotationX: 0,
        rotationY: 0,
        transformStyle: "preserve-3d",
      });

      if (
        !isActive ||
        reducedMotion ||
        !supportsFinePointer() ||
        !interactionTarget
      ) {
        gsap.set(body, { clearProps: "willChange" });
        return;
      }

      const rotateXTo = gsap.quickTo(body, "rotationX", {
        duration: 0.22,
        ease: "power2.out",
      });
      const rotateYTo = gsap.quickTo(body, "rotationY", {
        duration: 0.22,
        ease: "power2.out",
      });

      const reset = () => {
        rotateXTo(0);
        rotateYTo(0);
      };

      const handlePointerMove = (event: PointerEvent) => {
        if (event.pointerType !== "mouse") {
          reset();
          return;
        }

        const rect = interactionTarget.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return;

        const normalizedX = clamp(
          (event.clientX - rect.left) / rect.width - 0.5,
          -0.5,
          0.5,
        );
        const normalizedY = clamp(
          (event.clientY - rect.top) / rect.height - 0.5,
          -0.5,
          0.5,
        );

        rotateXTo(-normalizedY * maxRotation * 2);
        rotateYTo(normalizedX * maxRotation * 2);
      };

      gsap.set(body, { willChange: "transform" });
      interactionTarget.addEventListener("pointermove", handlePointerMove);
      interactionTarget.addEventListener("pointerleave", reset);
      interactionTarget.addEventListener("pointercancel", reset);

      return () => {
        interactionTarget.removeEventListener("pointermove", handlePointerMove);
        interactionTarget.removeEventListener("pointerleave", reset);
        interactionTarget.removeEventListener("pointercancel", reset);
        rotateXTo.tween.kill();
        rotateYTo.tween.kill();
        gsap.killTweensOf(body);
        gsap.set(body, {
          clearProps: "transform,transformStyle,willChange",
        });
      };
    },
    {
      dependencies: [
        interactionTarget,
        isActive,
        maxRotation,
        reducedMotion,
      ],
      scope: rootRef,
    },
  );

  return (
    <div
      className={cn("merry-3d-card", className)}
      data-merry-3d-card-active={isActive ? "true" : "false"}
      data-merry-3d-card-reduced-motion={reducedMotion ? "true" : "false"}
      ref={rootRef}
      style={{ perspective }}
    >
      <div className="merry-3d-card__body" ref={bodyRef}>
        {children}
      </div>
    </div>
  );
}
